import * as THREE from 'three';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraWorld } from '@/flora/FloraWorld';
import type { Terrain } from '@/terrain/Terrain';
import type { SiteObject } from '@/site/SiteObject';
import type { SiteObjectWorld } from '@/site/SiteObjectWorld';
import { footprintContains, syncExtents, totalHeight } from '@/site/SiteObject';
import {
  normalizeRing,
  ringArea,
  ringBounds,
  ringCentroid,
  type Point2,
} from '@/site/SiteFootprint';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import type { GridSpec } from '@/site/SiteGrid';
import {
  buildWandContext,
  type WandContext,
  type WandPlantSummary,
  type WandRegion,
  type WandStructureSummary,
  type WandTargetKind,
} from '@/wand/WandContext';

/**
 * The wand's canvas half: turn a gesture into a {@link WandContext}, and draw it on the ground.
 *
 * ## Click or drag, decided by the gesture itself
 *
 * A click names a point. A drag names a shape — the designer traces an outline and the request is
 * about everything inside it, which is the only way to say "make *this* a planted berm" about a bed
 * that is not a circle. There is no mode switch between the two: a press that never travels is a
 * click, and one that does is a lasso. That is worth more than a second toolbar button, because the
 * choice is made by the hand rather than before it.
 *
 * ## Why the mark is drawn in the scene
 *
 * The request is anchored to world coordinates, but the panel it is typed into is a floating DOM
 * element. The moment the camera orbits — and it will, because "look at what it did" is the next
 * thing anyone does — a panel alone stops saying anything about where the request is aimed. The ring
 * and the outline are scene geometry, so they stay on the ground they name, and both are re-draped
 * every frame so a result that sculpts that ground does not bury its own marker.
 *
 * ## What this does not do
 *
 * It does not build the prompt, call anything, or change the site. It converts pointer events into
 * facts, which is the only part of the wand that needs a camera, a raycaster, or a scene graph — and
 * therefore the only part that cannot be tested without WebGL. Everything downstream takes the record
 * this produces and is pure.
 */

/** Pixels of travel before a press stops being a click and becomes a lasso. */
const LASSO_THRESHOLD_PX = 7;

/** Metres between captured outline vertices. Coarse enough that a slow drag is not a point cloud. */
const LASSO_STEP_METERS = 0.4;

/** Vertices an outline is decimated to. Beyond this the prompt pays for detail no one drew. */
const MAX_REGION_VERTICES = 28;

/** Below this, a drag was a wobbly click and is treated as one. */
const MIN_REGION_AREA_METERS = 0.75;

/** Metres between sampled points along the drawn outline, so it follows the ground. */
const DRAPE_STEP_METERS = 0.5;

/** How far the outline floats above the ground, so it reads without z-fighting the surface. */
const LIFT_METERS = 0.05;

export interface WandToolDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  overlayRoot: THREE.Object3D;
  terrain: () => Terrain | null;
  world: () => FloraWorld;
  catalog: FloraCatalog;
  siteObjects: () => SiteObjectWorld;
  environment: EnvironmentManager;
  selectedIds: () => readonly string[];
  /**
   * The reference grid, when the designer has it on screen. `null` when it is hidden — the wand is
   * told about cell names exactly when the person typing the request can read them.
   */
  grid?: () => GridSpec | null;
  floraRoot: THREE.Object3D;
  siteRoot: THREE.Object3D;
  /** Fired when a click resolves to a target. The app opens the bubble. */
  onTarget: (context: WandContext, screen: { x: number; y: number }) => void;
}

/** A press in progress, from pointerdown until it resolves into a click or a lasso. */
interface Gesture {
  pointerId: number;
  startX: number;
  startY: number;
  /** Ground point under the press, kept so a click that ends over a plant still has a place. */
  origin: { x: number; z: number } | null;
  /** Ground points captured while dragging. Empty until the press passes the threshold. */
  path: Point2[];
  lasso: boolean;
}

export class WandTool {
  readonly overlayObject: THREE.Group;

  private readonly marker: THREE.Group;
  private readonly ring: THREE.Mesh;
  private readonly pulse: THREE.Mesh;
  private outline: THREE.LineSegments | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly disposables: (THREE.BufferGeometry | THREE.Material)[] = [];

  private active = false;
  private anchor: { x: number; z: number } | null = null;
  /** The outline currently drawn, in world metres. Kept so it can be re-draped as ground moves. */
  private region: Point2[] | null = null;
  private gesture: Gesture | null = null;
  private age = 0;

  private readonly onPointerDown = (event: PointerEvent): void => this.beginGesture(event);
  private readonly onPointerMove = (event: PointerEvent): void => this.extendGesture(event);
  private readonly onPointerUp = (event: PointerEvent): void => this.endGesture(event);
  private readonly onPointerCancel = (): void => this.abortGesture();

  constructor(private readonly deps: WandToolDeps) {
    this.overlayObject = new THREE.Group();
    this.overlayObject.name = 'wand-overlay';
    this.overlayObject.visible = false;

    // Two coplanar rings: a steady one that marks the point, and a wider one that breathes. The
    // second is the only ornament in the feature, and it earns its place by answering "is it
    // still working?" without a spinner sitting on top of the garden.
    this.marker = new THREE.Group();
    this.ring = this.disc(0.55, 0.72, 0xffd166, 0.9);
    this.pulse = this.disc(0.8, 0.9, 0xffd166, 0.35);
    this.marker.add(this.ring, this.pulse);
    this.overlayObject.add(this.marker);

    deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    deps.domElement.addEventListener('pointermove', this.onPointerMove);
    deps.domElement.addEventListener('pointerup', this.onPointerUp);
    deps.domElement.addEventListener('pointercancel', this.onPointerCancel);
  }

  get isActive(): boolean {
    return this.active;
  }

  // The pointer's appearance is not set here: the HUD draws a wand glyph for the whole time this
  // tool holds the canvas (see `ToolCursor`), and two owners of `style.cursor` would fight.
  activate(): void {
    this.active = true;
  }

  deactivate(): void {
    this.active = false;
    this.abortGesture();
    this.clearAnchor();
  }

  /** Show the ring at a world point and keep it there until cleared. */
  setAnchor(x: number, z: number): void {
    const terrain = this.deps.terrain();
    this.anchor = { x, z };
    this.overlayObject.visible = true;
    this.marker.position.set(x, (terrain?.sampleHeight(x, z) ?? 0) + 0.04, z);
  }

  clearAnchor(): void {
    this.anchor = null;
    this.overlayObject.visible = false;
    this.setOutline(null);
  }

  /**
   * Advance the ring's animation, and re-seat both marks if the ground moved under them.
   *
   * Driven from the render loop rather than from a CSS animation because these are scene geometry;
   * a wand result that sculpts the ground would otherwise leave its own marker buried — which is
   * exactly what "raise this area into a berm" does to the outline that asked for it.
   */
  tick(deltaSeconds: number, busy: boolean): void {
    if (!this.overlayObject.visible || !this.anchor) return;
    this.age += deltaSeconds;
    const terrain = this.deps.terrain();
    if (terrain) {
      this.marker.position.y = terrain.sampleHeight(this.anchor.x, this.anchor.z) + 0.04;
      this.redrape();
    }
    // At rest the outer ring simply sits there; while a request is in flight it swells and fades,
    // which is the entire "pizzazz" budget and is also the honest status indicator.
    const t = busy ? (this.age % 1.4) / 1.4 : 0;
    const scale = busy ? 1 + t * 1.6 : 1;
    this.pulse.scale.setScalar(scale);
    const material = this.pulse.material as THREE.MeshBasicMaterial;
    material.opacity = busy ? 0.45 * (1 - t) : 0.35;
    this.ring.rotation.z = busy ? this.age * 0.9 : 0;
    if (this.outline) {
      (this.outline.material as THREE.LineBasicMaterial).opacity = busy
        ? 0.55 + 0.35 * Math.sin(this.age * 4)
        : 0.9;
    }
  }

  /* --------------------------------- The gesture --------------------------------- */

  private beginGesture(event: PointerEvent): void {
    if (!this.active || event.button !== 0) return;
    const terrain = this.deps.terrain();
    if (!terrain) return;

    // Swallowed here rather than on release, so the press never reaches the orbit controls: a lasso
    // that also spins the camera would trace its outline across ground that is moving under it.
    event.preventDefault();
    event.stopPropagation();

    // Captured so an outline traced past the edge of the canvas still ends on this element's
    // pointerup rather than being abandoned mid-drag with a half-drawn shape on the ground.
    try {
      this.deps.domElement.setPointerCapture(event.pointerId);
    } catch {
      // Not every host implements capture; the gesture still works, it just ends at the edge.
    }

    this.raycaster.setFromCamera(this.toNdc(event.clientX, event.clientY), this.deps.getCamera());
    this.gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: this.pickGround(terrain),
      path: [],
      lasso: false,
    };
  }

  private extendGesture(event: PointerEvent): void {
    const gesture = this.gesture;
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const terrain = this.deps.terrain();
    if (!terrain) return;

    if (!gesture.lasso) {
      const travelled = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
      if (travelled < LASSO_THRESHOLD_PX) return;
      gesture.lasso = true;
      if (gesture.origin) gesture.path.push({ x: gesture.origin.x, z: gesture.origin.z });
      // The outline replaces the previous request's mark the moment drawing starts, so there are
      // never two claims on screen about what the wand is aimed at.
      this.clearAnchor();
      this.overlayObject.visible = true;
      this.marker.visible = false;
    }

    this.raycaster.setFromCamera(this.toNdc(event.clientX, event.clientY), this.deps.getCamera());
    const point = this.pickGround(terrain);
    if (!point) return;
    const last = gesture.path[gesture.path.length - 1];
    // Decimated as it is captured rather than afterwards: a slow drag emits a move event per frame,
    // and a hundred vertices within a metre are a point cloud, not an outline.
    if (last && Math.hypot(point.x - last.x, point.z - last.z) < LASSO_STEP_METERS) return;
    gesture.path.push({ x: point.x, z: point.z });
    this.setOutline(gesture.path);
  }

  private endGesture(event: PointerEvent): void {
    const gesture = this.gesture;
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    this.gesture = null;
    this.marker.visible = true;

    const terrain = this.deps.terrain();
    if (!terrain) {
      this.clearAnchor();
      return;
    }

    const ring = gesture.lasso ? closedRing(gesture.path) : null;
    if (ring) {
      this.resolveArea(ring, terrain, event);
      return;
    }

    // A drag that enclosed nothing is a wobbly click, and answering it with silence would be the
    // tool refusing a gesture the designer cannot tell apart from one that works.
    this.setOutline(null);
    this.raycaster.setFromCamera(this.toNdc(event.clientX, event.clientY), this.deps.getCamera());
    this.resolveClick(terrain, event);
  }

  private abortGesture(): void {
    if (!this.gesture) return;
    const drawing = this.gesture.lasso;
    this.gesture = null;
    this.marker.visible = true;
    // A lasso has already taken the previous request's ring down; leaving the marker on screen at
    // the place it used to mean would be a claim about an aim that no longer exists.
    if (drawing) this.clearAnchor();
    else this.setOutline(null);
  }

  /* -------------------------------- Resolving -------------------------------- */

  /**
   * Resolve a click to a target.
   *
   * Order matters: plants are tested first, then structures, then the ground. A click on a tree in
   * front of a shed is about the tree — it is the thing the pointer is over.
   */
  private resolveClick(terrain: Terrain, event: PointerEvent): void {
    const plantId = this.pickId(this.deps.floraRoot, 'floraInstanceId');
    const structureId = this.pickId(this.deps.siteRoot, 'siteObjectId');
    const ground = this.pickGround(terrain);

    // Prefer the ground hit for the anchor point — a click on a canopy should aim at the ground
    // beneath the pointer, not at the leaf that happened to be hit.
    const point = ground ?? this.fallbackPoint(plantId, structureId);
    if (!point) return;

    const kind: WandTargetKind = plantId ? 'plant' : structureId ? 'structure' : 'ground';
    const structure = structureId ? this.deps.siteObjects().get(structureId) : undefined;

    this.emit(terrain, point, event, {
      kind,
      ...(plantId ? { plantId } : {}),
      ...(structure ? { structure: this.summariseStructure(structure, point) } : {}),
    });
  }

  /** Resolve a drawn outline to an area target, anchored at its centroid. */
  private resolveArea(ring: Point2[], terrain: Terrain, event: PointerEvent): void {
    const centroid = ringCentroid(ring);
    const bounds = ringBounds(ring);
    const region: WandRegion = {
      polygon: ring.map((point) => [round(point.x, 2), round(point.z, 2)] as [number, number]),
      areaMeters: round(ringArea(ring), 1),
      widthMeters: round(bounds.maxX - bounds.minX, 2),
      lengthMeters: round(bounds.maxZ - bounds.minZ, 2),
      radiusMeters: round(
        Math.max(...ring.map((point) => Math.hypot(point.x - centroid.x, point.z - centroid.z))),
        2,
      ),
    };

    this.region = ring;
    this.emit(terrain, centroid, event, { kind: 'area', region });
  }

  /** Build the context and hand it to the panel. The one path both gestures end in. */
  private emit(
    terrain: Terrain,
    point: { x: number; z: number },
    event: PointerEvent,
    target: {
      kind: WandTargetKind;
      plantId?: string;
      structure?: WandStructureSummary;
      region?: WandRegion;
    },
  ): void {
    const grid = this.deps.grid?.() ?? null;

    const context = buildWandContext({
      site: {
        widthMeters: terrain.widthMeters,
        lengthMeters: terrain.lengthMeters,
        coverage: terrain.coverage(),
        reliefMeters: relief(terrain),
      },
      target: {
        ...target,
        point,
        groundHeightMeters: terrain.sampleHeight(point.x, point.z),
        surface: terrain.sampleSurface(point.x, point.z),
      },
      environment: this.deps.environment.state,
      plants: this.summarisePlants(),
      structures: this.deps
        .siteObjects()
        .all()
        .map((object) => this.summariseStructure(object, point)),
      selectedIds: this.deps.selectedIds(),
      ...(grid ? { grid } : {}),
    });

    this.age = 0;
    this.setAnchor(point.x, point.z);
    if (target.region) this.setOutline(this.region);
    this.deps.onTarget(context, { x: event.clientX, y: event.clientY });
  }

  /* --------------------------------- The outline --------------------------------- */

  /**
   * Draw an outline draped over the ground, or clear it.
   *
   * Draped rather than flat for the reason the reference grid is: a shape drawn on the y=0 plane
   * across a site whose whole first act is sculpting one is half-buried and half-floating, and the
   * ground it appears to enclose is not the ground it does enclose.
   */
  private setOutline(ring: readonly Point2[] | null): void {
    if (this.outline) {
      this.outline.removeFromParent();
      this.outline.geometry.dispose();
      (this.outline.material as THREE.Material).dispose();
      this.outline = null;
    }
    this.region = ring && ring.length >= 2 ? ring.map((point) => ({ ...point })) : null;
    if (!this.region) return;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.drapedPositions(), 3));
    const material = new THREE.LineBasicMaterial({
      color: 0xffd166,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });
    this.outline = new THREE.LineSegments(geometry, material);
    this.outline.renderOrder = 11;
    this.overlayObject.add(this.outline);
    this.overlayObject.visible = true;
  }

  /** Re-sample the outline's heights against ground that may have moved. */
  private redrape(): void {
    if (!this.outline || !this.region) return;
    this.outline.geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(this.drapedPositions(), 3),
    );
    this.outline.geometry.computeBoundingSphere();
  }

  /**
   * The outline as line segments following the terrain.
   *
   * Closed only once the shape is finished: while the pointer is still down the path is an open
   * trail, and drawing the closing edge would show the designer a shape they have not drawn yet.
   */
  private drapedPositions(): number[] {
    const ring = this.region ?? [];
    const terrain = this.deps.terrain();
    const sample = (x: number, z: number): number => (terrain?.sampleHeight(x, z) ?? 0) + LIFT_METERS;
    const closed = this.gesture === null;
    const out: number[] = [];

    const edges = closed ? ring.length : ring.length - 1;
    for (let i = 0; i < edges; i += 1) {
      const a = ring[i] as Point2;
      const b = ring[(i + 1) % ring.length] as Point2;
      const length = Math.hypot(b.x - a.x, b.z - a.z);
      const steps = Math.max(1, Math.ceil(length / DRAPE_STEP_METERS));
      for (let s = 0; s < steps; s += 1) {
        const t0 = s / steps;
        const t1 = (s + 1) / steps;
        const x0 = a.x + (b.x - a.x) * t0;
        const z0 = a.z + (b.z - a.z) * t0;
        const x1 = a.x + (b.x - a.x) * t1;
        const z1 = a.z + (b.z - a.z) * t1;
        out.push(x0, sample(x0, z0), z0, x1, sample(x1, z1), z1);
      }
    }
    return out;
  }

  /**
   * Every plant, as facts. Distances are filled in by `buildWandContext`, which knows the click
   * point; this only supplies what the world knows about each plant.
   */
  private summarisePlants(): WandPlantSummary[] {
    const world = this.deps.world();
    return world.all().map((instance) => {
      const definition = this.deps.catalog.find(instance.floraDefinitionId);
      const growth = world.growthState(instance);
      return {
        id: instance.id,
        speciesId: instance.floraDefinitionId,
        name: definition?.commonNames[0] ?? definition?.botanicalName ?? instance.floraDefinitionId,
        x: instance.position.x,
        z: instance.position.z,
        distanceMeters: 0,
        maturity: instance.maturity,
        heightMeters: round(growth.heightMeters, 2),
        spreadMeters: round(growth.spreadMeters, 2),
        selected: false,
      };
    });
  }

  private summariseStructure(object: SiteObject, from: { x: number; z: number }): WandStructureSummary {
    const sized = syncExtents(object);
    return {
      id: object.id,
      name: object.name,
      kind: object.kind,
      x: round(object.position.x, 2),
      z: round(object.position.z, 2),
      widthMeters: round(sized.widthMeters, 2),
      depthMeters: round(sized.depthMeters, 2),
      heightMeters: round(totalHeight(object), 2),
      distanceMeters: round(Math.hypot(object.position.x - from.x, object.position.z - from.z), 2),
    };
  }

  private toNdc(clientX: number, clientY: number): THREE.Vector2 {
    const rect = this.deps.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private pickGround(terrain: Terrain): { x: number; z: number } | null {
    const hits = this.raycaster.intersectObject(terrain.mesh, false);
    const point = hits[0]?.point;
    if (!point) return null;
    return {
      x: clamp(point.x, -terrain.widthMeters / 2, terrain.widthMeters / 2),
      z: clamp(point.z, -terrain.lengthMeters / 2, terrain.lengthMeters / 2),
    };
  }

  private pickId(root: THREE.Object3D, key: string): string | null {
    const hits = this.raycaster.intersectObjects(root.children, true);
    let current: THREE.Object3D | null = hits[0]?.object ?? null;
    while (current) {
      const id = current.userData[key] as unknown;
      if (typeof id === 'string') return id;
      current = current.parent;
    }
    return null;
  }

  /**
   * When the ray missed the ground — looking at a building from below the horizon, say — fall back to
   * the hit object's own position, so a click that visibly landed on something is never discarded.
   */
  private fallbackPoint(plantId: string | null, structureId: string | null): { x: number; z: number } | null {
    if (plantId) {
      const instance = this.deps.world().get(plantId);
      if (instance) return { x: instance.position.x, z: instance.position.z };
    }
    if (structureId) {
      const object = this.deps.siteObjects().get(structureId);
      if (object) return { x: object.position.x, z: object.position.z };
    }
    return null;
  }

  private disc(inner: number, outer: number, color: number, opacity: number): THREE.Mesh {
    const geometry = new THREE.RingGeometry(inner, outer, 48);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    this.disposables.push(geometry, material);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 10;
    return mesh;
  }

  dispose(): void {
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.removeEventListener('pointerup', this.onPointerUp);
    this.deps.domElement.removeEventListener('pointercancel', this.onPointerCancel);
    this.setOutline(null);
    this.overlayObject.removeFromParent();
    for (const item of this.disposables) item.dispose();
    this.disposables.length = 0;
  }
}

/**
 * Turn a captured trail into a usable ring, or null when it does not enclose anything.
 *
 * Decimation is uniform rather than curvature-aware. A hand-drawn bed has no vertices that matter
 * more than the others — it is all curve — so keeping "the important ones" would mean inventing a
 * definition of important, and dropping every nth point loses nothing a garden can tell.
 *
 * Returning null for a scribble is deliberate: {@link WandTool.endGesture} then reads the gesture as
 * a click, so a shaky press still opens the panel instead of appearing to do nothing.
 */
export function closedRing(path: readonly Point2[]): Point2[] | null {
  if (path.length < 3) return null;

  let sampled = path;
  if (sampled.length > MAX_REGION_VERTICES) {
    const stride = sampled.length / MAX_REGION_VERTICES;
    const thinned: Point2[] = [];
    for (let i = 0; i < MAX_REGION_VERTICES; i += 1) {
      thinned.push(sampled[Math.min(sampled.length - 1, Math.floor(i * stride))] as Point2);
    }
    sampled = thinned;
  }

  const ring = normalizeRing(sampled);
  if (ring.length < 3 || ringArea(ring) < MIN_REGION_AREA_METERS) return null;
  return ring;
}

/** Metres between the lowest and highest ground on the site. */
function relief(terrain: Terrain): number {
  let min = Infinity;
  let max = -Infinity;
  for (const height of terrain.field.heights) {
    if (!Number.isFinite(height)) continue;
    min = Math.min(min, height);
    max = Math.max(max, height);
  }
  return Number.isFinite(min) && Number.isFinite(max) ? round(max - min, 2) : 0;
}

/** Whether a world point falls inside a site object — used by the fallback structure test. */
export function structureAt(
  objects: readonly SiteObject[],
  x: number,
  z: number,
): SiteObject | undefined {
  return objects.find((object) => footprintContains(object, x, z));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, places = 3): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
